import { saloonTokens } from 'styles/saloonTheme';

// Shared saloon action styling for the action row (ActionButton) AND the
// pre-action chips (CheckboxAction) so both read as one type/colour system
// (EN-10265). Each variant is a dark leather/blackboard fill with an inset
// dashed "stitch" edge tinted to the action's accent colour:
//   fold  → burnt-leather red      check → blackboard blue
//   call  → saloon green           raise/allin → primary orange
//   - `bg`     : button background (gradient)
//   - `border` : solid outer border colour (accent-tinted)
//   - `dash`   : inset dashed stitch colour (accent-tinted)
//   - `text`   : label/amount colour
//   - `shadow` : per-variant text-shadow
export type ActionVariantKey = 'fold' | 'check' | 'call' | 'raise' | 'allin';

export type ActionVariant = {
  bg: string;
  border: string;
  dash: string;
  text: string;
  shadow: string;
};

export const actionVariants: Record<ActionVariantKey, ActionVariant> = {
  // Burnt-leather red
  fold: {
    bg: 'linear-gradient(180deg, #6b1a14, #3a0a08)',
    border: 'rgba(214,120,110,0.55)',
    dash: 'rgba(241,227,199,0.5)',
    text: saloonTokens.bone,
    shadow: '0 2px 0 rgba(0,0,0,0.6)'
  },
  // Blackboard blue
  check: {
    bg: 'linear-gradient(180deg, #1a2438, #0a1322)',
    border: 'rgba(96,150,230,0.6)',
    dash: 'rgba(120,170,240,0.6)',
    text: saloonTokens.bone,
    shadow: '0 2px 0 rgba(0,0,0,0.6)'
  },
  // Saloon green — the "money / commit" affordance for Call.
  call: {
    bg: 'linear-gradient(180deg, #2f6d49, #163d28)',
    border: 'rgba(120,210,150,0.55)',
    dash: 'rgba(150,220,170,0.55)',
    text: saloonTokens.bone,
    shadow: '0 2px 0 rgba(0,0,0,0.6)'
  },
  // Primary orange
  raise: {
    bg: 'linear-gradient(180deg, #ffb45c 0%, #e8821f 55%, #8a4310 100%)',
    border: '#2a0805',
    dash: 'rgba(74,35,10,0.55)',
    text: saloonTokens.ink,
    shadow: '0 1px 0 rgba(255,220,160,0.5)'
  },
  allin: {
    bg: 'linear-gradient(180deg, #ffb45c 0%, #e8821f 55%, #8a4310 100%)',
    border: '#2a0805',
    dash: 'rgba(74,35,10,0.55)',
    text: saloonTokens.ink,
    shadow: '0 1px 0 rgba(255,220,160,0.5)'
  }
};

// Map a button label to its accent variant. Used by the pre-action chips
// (which are label-driven) so "Check/Fold" reads red, "Check" blue, "Call
// Any" green, "All In" orange — matching the action row. Order matters:
// "all in" and "fold" are checked before the bare "check" fallback so
// "check/fold" resolves to fold (red), not check.
export const variantFromLabel = (label?: string): ActionVariantKey => {
  const l = (label || '').toLowerCase();
  if (l.includes('all in')) return 'allin';
  if (l.includes('fold')) return 'fold';
  if (l.includes('call')) return 'call';
  if (l.includes('raise') || l.includes('bet')) return 'raise';
  // Post Blind is the affirmative "commit and play" choice of the join pair, so
  // it takes the primary orange fill (like Raise / All In); its sibling "Wait for
  // Big Blind" is the passive option and keeps the blue `check` fallback below.
  // Checked before the fallback and specific to "post blind" so it never catches
  // "wait for big blind" (both contain "blind").
  if (l.includes('post blind')) return 'raise';
  return 'check';
};

// Split a combined action label ("Call $0.38", "All In 1,200") into a bold
// label line + a smaller amount line for the two-line action-button design.
// Plain labels (Fold / Check / Bet / Raise) have no trailing amount and
// return an empty `amount`, so the button stays single-line.
export const splitLabelAmount = (text: string): { label: string; amount: string } => {
  // The trailing group must be an AMOUNT and nothing else — an optional currency
  // sign, then digits, separators and spaces to the end of the string. It used to
  // be `[$\d].*`, "starts like a number, then anything", which also split any
  // label whose last word merely BEGAN with a digit: EN-663's "RUN IT 1X" came out
  // as a bold "RUN IT" over a small "1X", two lines for a three-word label that
  // AC-5.2 pins verbatim.
  //
  // Nothing real is lost by the tightening. Every amount this renders is built by
  // `currencyFormatter` or `toLocaleStringNumber` ("Call $1,250.50", "All In
  // 15,000"), and an "amount" containing letters was never one.
  const match = /^(.*?)\s+(\$?\d[\d,.\s]*)$/.exec(text || '');
  return match ? { label: match[1], amount: match[2] } : { label: text || '', amount: '' };
};
